// Shared check-in helpers for private bookings (single guest count, no ticket types).
// States: pending (0 checked in) → partial (some) → in (all checked in) → out (checked out).

export const CHECK_IN_STATE = {
  PENDING: "pending",
  PARTIAL: "partial",
  IN: "in",
  OUT: "out"
}

// Derive the row state from counts.
//   pending : nobody checked in
//   in      : everyone checked in, nobody checked out → next action is check-out
//   out     : everyone checked out → next action is reset
//   partial : any in-between (some checked in, or some—but not all—checked out) → dropdown
export const deriveCheckInState = (checkedInCount = 0, totalGuests = 0, checkedOutCount = 0) => {
  if (totalGuests > 0 && checkedOutCount >= totalGuests) return CHECK_IN_STATE.OUT
  if (checkedInCount <= 0) return CHECK_IN_STATE.PENDING
  if (checkedInCount >= totalGuests && checkedOutCount <= 0) return CHECK_IN_STATE.IN
  return CHECK_IN_STATE.PARTIAL
}

// Tooltip copy per state (matches the design's row hints).
export const CHECK_IN_TOOLTIP = {
  [CHECK_IN_STATE.PENDING]: "Click to check in",
  [CHECK_IN_STATE.PARTIAL]: "Partially checked-in, click to check-in or out",
  [CHECK_IN_STATE.IN]: "Checked-in, click to check out",
  [CHECK_IN_STATE.OUT]: "Checked-out, click to reset"
}

// Read-only status labels (no "click to…" — used where check-in is display-only).
export const CHECK_IN_STATUS_LABEL = {
  [CHECK_IN_STATE.PENDING]: "Not checked in",
  [CHECK_IN_STATE.PARTIAL]: "Partially checked in",
  [CHECK_IN_STATE.IN]: "Checked in",
  [CHECK_IN_STATE.OUT]: "Checked out"
}

// Normalise a booking's guest-type breakdown into the shape the check-in/out modals render:
// [{ key, label, total, checkedInCount, checkedOutCount }]. Uses the backend `guest_types`
// array when present; otherwise a single synthetic "Guests" row from the aggregate counts, so
// the modals keep working before per-type data lands.
export const buildGuestTypes = (rawTypes, fallbackTotal = 0, fallbackCheckedIn = 0, fallbackCheckedOut = 0) => {
  if (Array.isArray(rawTypes) && rawTypes.length > 0) {
    return rawTypes.map((type) => ({
      key: String(type.key ?? type.id ?? type.label),
      label: type.label ?? type.name ?? "Guests",
      total: type.total ?? type.count ?? 0,
      checkedInCount: type.checked_in_count ?? 0,
      checkedOutCount: type.checked_out_count ?? 0
    }))
  }
  return [{ key: "guests", label: "Guests", total: fallbackTotal, checkedInCount: fallbackCheckedIn, checkedOutCount: fallbackCheckedOut }]
}

export const sumCheckedIn = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.checkedInCount ?? 0), 0)
export const sumCheckedOut = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.checkedOutCount ?? 0), 0)
export const sumGuests = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.total ?? 0), 0)
